<script>
import DoughnutChart from '@components/charts/doughnut-chart.vue';
import NotificationProgramSummary from '@components/notifications/notification-program-summary.vue';
import CardProgramSummary from '@components/cards/card-program-summary.vue';

export default {
  components: {
    DoughnutChart,
    NotificationProgramSummary,
    CardProgramSummary,
  },
  props: {
    client: {
      type: Object,
      required: true,
      default: () => {},
    },
  },
  data() {
    return {
      chartData: {
        datasets: [
          {
            backgroundColor: ['#4059E1', '#deded9'],
            data: [89, 11],
          },
        ],
      },
      chartOpts: {
        cutoutPercentage: 88,
        tooltips: {
          enabled: false,
        },
        legend: {
          display: false,
        },
      },
    };
  },
  computed: {
    workoutSatisfaction() {
      return this.client?.latestPostWorkoutReview?.workoutSatisfaction
        ? this.client.latestPostWorkoutReview.workoutSatisfaction
        : 0;
    },
    remainingEnergyPercent() {
      return this.client?.latestPostWorkoutReview?.remainingEnergyPercent
        ? this.client.latestPostWorkoutReview.remainingEnergyPercent
        : 0;
    },
    progressTowardsGoal() {
      return this.client?.latestPostWorkoutReview?.progressTowardsGoal
        ? this.client.latestPostWorkoutReview.progressTowardsGoal
        : 0;
    },
    anyOtherFeedback() {
      return this.client?.latestPostWorkoutReview?.anyOtherFeedback
        ? this.client.latestPostWorkoutReview.anyOtherFeedback
        : null;
    },
  },
};
</script>

<template>
  <div class="last-session-wrapper h-100">
    <CardProgramSummary :title="'Last Session'">
      <template v-slot:icon>
        <svg width="21" height="23" viewBox="0 0 21 23" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:class="'icon'" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path clip-rule="evenodd" d="M4.18 7.87c2.032 0 3.678-1.761 3.678-3.935C7.858 1.762 6.212 0 4.181 0S.503 1.762.503 3.935c0 2.174 1.647 3.936 3.678 3.936zm0-2.66c.678 0 1.226-.595 1.226-1.33 0-.735-.548-1.33-1.225-1.33s-1.226.595-1.226 1.33c0 .735.549 1.33 1.226 1.33zM.125 10.03a2.07 2.07 0 00.172 1.77l5.737 9.528a1.694 1.694 0 002.487.467l9.845-7.607A4.974 4.974 0 0020.29 9.98l-.032-.577a4.186 4.186 0 00-3.726-3.932 4.279 4.279 0 00-4.018 1.872L7.387 15 2.786 9.304a1.487 1.487 0 00-2.555.428l-.107.297z"/></svg>
      </template>
      <div class="last-session-block row">
        <div class="doughnut-chart-wrapper col-4 py-3">
          <div class="doughnut-chart-data">
            <span class="chart-num">
              {{ progressTowardsGoal }}<small>%</small>
            </span>
            <DoughnutChart
              class="doughnut-chart"
              :data="{
                ...chartData,
                datasets: [
                  {
                    ...chartData.datasets[0],
                    data: [progressTowardsGoal, 100 - progressTowardsGoal],
                  },
                ],
              }"
              :options="chartOpts"
            />
          </div>
          <h6 class="text-center my-3">GOAL PROGRESS</h6>
        </div>
        <div class="doughnut-chart-wrapper col-4 py-3">
          <div class="doughnut-chart-data">
            <span class="chart-num">
              {{ workoutSatisfaction }}<small>%</small>
            </span>
            <DoughnutChart
              class="doughnut-chart"
              :data="{
                ...chartData,
                datasets: [
                  {
                    ...chartData.datasets[0],
                    data: [workoutSatisfaction, 100 - workoutSatisfaction],
                  },
                ],
              }"
              :options="chartOpts"
            />
          </div>
          <h6 class="text-center my-3">WORKOUT SATISFACTION</h6>
        </div>
        <div class="doughnut-chart-wrapper col-4 py-3">
          <div class="doughnut-chart-data">
            <span class="chart-num">
              {{ remainingEnergyPercent }}<small>%</small>
            </span>
            <DoughnutChart
              class="doughnut-chart"
              :data="{
                ...chartData,
                datasets: [
                  {
                    ...chartData.datasets[0],
                    data: [
                      remainingEnergyPercent,
                      100 - remainingEnergyPercent,
                    ],
                  },
                ],
              }"
              :options="chartOpts"
            />
          </div>
          <h6 class="text-center my-3">ENERGY LEFT</h6>
        </div>
      </div>
      <template v-if="anyOtherFeedback">
        <NotificationProgramSummary heading="CLIENT WORKOUT FEEDBACK">
          <div class="content mb-0">{{ anyOtherFeedback }}</div>
        </NotificationProgramSummary>
      </template>
      <template v-else>
        <NotificationProgramSummary type="blue" :icon="true">
          <template v-slot:icon>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" v-bind:class="'icon'" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path data-name="Subtraction 2" d="M12 24A12 12 0 013.515 3.515a12 12 0 1116.97 16.97A11.922 11.922 0 0112 24zm-.886-8.946V17h1.96v-1.946zM12.15 8.5a1.509 1.509 0 01.952.266.882.882 0 01.336.728 1.6 1.6 0 01-.182.742 3.785 3.785 0 01-.616.84 4.972 4.972 0 00-.84 1.224 3.1 3.1 0 00-.238 1.219v.56h1.064l.056-.392a2.675 2.675 0 01.322-.9 4.662 4.662 0 01.91-1.05 4.418 4.418 0 001.008-1.218 2.563 2.563 0 00.294-1.2 2.016 2.016 0 00-.378-1.219 2.424 2.424 0 00-1.038-.816 3.781 3.781 0 00-1.541-.293 5.181 5.181 0 00-1.778.321 4.483 4.483 0 00-1.456.854l.591 1.344A3.807 3.807 0 0112.15 8.5z"/></svg>
          </template>
          <h5>Client Feedback</h5>
          <div class="content">
            Last session’s feedback will be displayed here.
          </div>
        </NotificationProgramSummary>
      </template>
    </CardProgramSummary>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';
.doughnut-chart {
  height: 80px;
  width: 80px;
}
.doughnut-chart-data {
  display: flex;
  height: 80px;
  margin: 0 auto;
  position: relative;
  width: 80px;
}
.chart-num {
  color: $color-black;
  font-size: 18px;
  font-weight: 600;
  left: 0;
  line-height: 80px;
  position: absolute;
  right: 0;
  text-align: center;
  top: 0;
}
.notification-title {
  color: red;
  font-size: 16px;
  font-weight: 900;
  margin-bottom: 16px;
}
</style>
